import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faCalendar,
    faUser,
    faPhone,
    faExclamation,
    faCheck,
    faPlus,
    faTasks,
    faChevronDown,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../../../components/general/form/CustomForm";
import WhatsAppReminder from "../../../components/general/auxiliar/ui/WhatsAppReminder";
import PaginatorComponent from "../../../components/general/auxiliar/ui/PaginatorComponent";
import Collapse from "../../../components/general/auxiliar/collapse/Collapse";
import EmptyTable from "../../../components/general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../components/general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../components/general/auxiliar/table/OverviewTable";
import OverviewCardList from "../../../components/general/auxiliar/table/OverviewCard";
import TableBadge from "../../../components/general/auxiliar/table/TableBadge";
import Dropdown from "../../../components/general/auxiliar/ui/Dropdown";
import useFormWithDisable from "../../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../../auxiliar/customHooks/usePaginationFilterEffects";
import { handleSubmit, getTableState } from "../../../auxiliar/auxFunctions";
import { getAllTasks, putTaskStatus } from "../../../actions/task";

const OverviewTasks = () => {
    const {
        register,
        watch,
        setValue,
        errors,
        handleSubmit: handleFormSubmit,
        isSubmitting,
    } = useFormWithDisable();
    const writtenFilter = {
        topic: watch("topic") || "",
        order: watch("order") || "date_asc",
        temporality: watch("temporality") || "all",
        period: watch("period") || "days",
        duration: watch("duration") || "",
        starting_date: watch("starting_date") || "",
        finishing_date: watch("finishing_date") || "",
        display_finished: watch("display_finished") || false,
        exclude_autogenerated: watch("exclude_autogenerated") || false,
    };

    const navigate = useNavigate();
    const { id: clinicId } = useSelector((state) => state.activeClinic);

    const [tasks, setTasks] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        clinic_id: clinicId,
        page: 1,
        per_page: 10,
        order: "date_asc",
        updateCount: 0,
    });

    const update = () => setFilter({ ...filter, updateCount: filter.updateCount + 1 });

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllTasks,
        setTasks,
        setFilter,
    );

    // Helper function to check if task is expired
    const isTaskExpired = (task) => {
        if (task.finished) return false;
        const taskDate = new Date(task.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        taskDate.setHours(0, 0, 0, 0);
        return taskDate < today;
    };

    // Helper function to get task status
    const getTaskStatus = (task) => {
        if (task.finished) return "completed";
        if (isTaskExpired(task)) return "expired";
        return "pending";
    };

    // Helper function to get status dot color
    const getStatusDotColor = (status) => {
        switch (status) {
            case "completed":
                return "bg-success";
            case "pending":
                return "bg-warning";
            case "expired":
                return "bg-error";
            default:
                return "bg-gray-500";
        }
    };

    // Helper function to get status tooltip text
    const getStatusTooltip = (status) => {
        switch (status) {
            case "completed":
                return "Completada";
            case "pending":
                return "Pendiente";
            case "expired":
                return "Vencida - requiere atención";
            default:
                return "";
        }
    };

    // Table columns configuration
    const columns = [
        { label: "Tema", icon: faTasks, width: "w-[25%]" },
        { label: "Paciente", icon: faUser, cellClass: "hidden sm:table-cell", width: "w-[26%]" },
        { label: "Teléfono", icon: faPhone, cellClass: "hidden md:table-cell", width: "w-[12%]" },
        { label: "Fecha", icon: faCalendar, width: "w-[12%]" },
        {
            label: "Prioridad",
            icon: faExclamation,
            cellClass: "hidden md:table-cell",
            width: "w-[12%]",
        },
    ];

    // Function to get row data for table
    const getRowData = (task, taskId) => {
        const finishedId = `finished_${taskId}`;
        setValue(finishedId, task.finished);

        // Priority badge styling
        const getPriorityBadge = (priority) => {
            const priorityNum = parseInt(priority);
            let variant = "success";
            let text = "";

            if (priorityNum >= 8) {
                variant = "error";
                text = `${priority} - Crítica`;
            } else if (priorityNum >= 6) {
                variant = "warning";
                text = `${priority} - Alta`;
            } else if (priorityNum >= 4) {
                variant = "info";
                text = `${priority} - Media`;
            } else {
                variant = "success";
                text = `${priority} - Baja`;
            }

            return (
                <TableBadge key={`${taskId}-priority`} variant={variant} size="sm">
                    {text}
                </TableBadge>
            );
        };

        return [
            // Tema (with completion status dot)
            <div key={`${taskId}-topic`} className="flex items-center gap-2">
                <div
                    className={`tooltip w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(getTaskStatus(task))}`}
                    data-tip={getStatusTooltip(getTaskStatus(task))}
                ></div>
                <div className="font-semibold text-base-content truncate" title={task.topic}>
                    {task.topic}
                </div>
            </div>,
            // Paciente
            <div key={`${taskId}-patient`} className="text-base-content/70 truncate">
                {task.patient_name && task.patient_surnames
                    ? `${task.patient_name} ${task.patient_surnames}`
                    : "-"}
            </div>,
            // Teléfono
            <div key={`${taskId}-phone`} className="text-base-content/70">
                {task.patient_phone_number ? (
                    <div className="flex items-center gap-1">
                        <span>{task.patient_phone_number}</span>
                        <WhatsAppReminder link={task.whatsapp_link} />
                    </div>
                ) : (
                    "-"
                )}
            </div>,
            // Fecha
            <div
                key={`${taskId}-date`}
                className="text-sm text-base-content truncate"
                title={task.date}
            >
                {task.date}
            </div>,
            // Prioridad (with colored badge)
            getPriorityBadge(task.priority),
        ];
    };

    // Function to get detail path
    const getDetailPath = (taskId) => `/task/${taskId}`;

    // Helper function to create dropdown items for task actions
    const createTaskDropdownItems = (task, taskId) => [
        {
            key: "details",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Detalles
                </div>
            ),
            onClick: () => handleSubmit(navigate, `/task/${taskId}`)(),
        },
        {
            key: "toggle-status",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    {task.finished ? "Marcar pendiente" : "Marcar hecha"}
                </div>
            ),
            onClick: () => handleFormSubmit(putTaskStatus, taskId, update)(),
            disabled: isSubmitting,
        },
    ];

    // Shared dropdown renderer
    const renderTaskDropdown = (task, taskId, triggerSize = "sm") => {
        const dropdownItems = createTaskDropdownItems(task, taskId);

        const trigger = (
            <div className={`btn btn-primary btn-${triggerSize}`}>
                <FontAwesomeIcon icon={faChevronDown} className="text-base-100" />
                {triggerSize === "sm" && <span className="hidden xl:inline"> Opciones</span>}
            </div>
        );

        return (
            <Dropdown
                trigger={trigger}
                items={dropdownItems}
                position="bottom-right"
                width="w-52"
                closeOnItemClick={true}
            />
        );
    };

    // Custom actions renderer for table (dropdown with text)
    const renderTableActions = (task, taskId) => renderTaskDropdown(task, taskId, "sm");

    // Custom actions renderer for mobile cards (smaller dropdown)
    const renderCardActions = (task, taskId) => renderTaskDropdown(task, taskId, "xs");

    // Card render functions
    const renderTitle = (task) => (
        <div className="flex items-center gap-2">
            <div
                className={`tooltip w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(getTaskStatus(task))}`}
                data-tip={getStatusTooltip(getTaskStatus(task))}
            ></div>
            <span>{task.topic}</span>
        </div>
    );

    const renderSubtitle = (task) => (
        <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCalendar}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                {isTaskExpired(task) && (
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-error ml-1"
                        title="Tarea vencida"
                    />
                )}
                <span className="text-xs sm:text-sm">{task.date}</span>
            </div>
            {task.patient_name && (
                <div className="flex items-center gap-1 text-base-content/70 sm:hidden">
                    <FontAwesomeIcon
                        icon={faUser}
                        className="w-3 h-3 text-base-content/40 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm">
                        {task.patient_name} {task.patient_surnames}
                    </span>
                </div>
            )}
        </div>
    );

    const renderFooter = (task) => {
        // Priority badge styling for cards
        const getPriorityBadge = (priority) => {
            const priorityNum = parseInt(priority);
            let variant = "success";
            let text = "";

            if (priorityNum >= 8) {
                variant = "error";
                text = `${priority} - Crítica`;
            } else if (priorityNum >= 6) {
                variant = "warning";
                text = `${priority} - Alta`;
            } else if (priorityNum >= 4) {
                variant = "info";
                text = `${priority} - Media`;
            } else {
                variant = "success";
                text = `${priority} - Baja`;
            }

            return (
                <TableBadge variant={variant} size="xs">
                    {text}
                </TableBadge>
            );
        };

        return (
            <div className="space-y-2 text-xs sm:text-sm">
                {task.patient_name && task.patient_surnames && (
                    <div className="flex items-center gap-2 text-base-content/70">
                        <FontAwesomeIcon
                            icon={faUser}
                            className="w-3 h-3 text-base-content/40 flex-shrink-0"
                        />
                        <span className="truncate">
                            {task.patient_name} {task.patient_surnames}
                        </span>
                    </div>
                )}
                {task.patient_phone_number && (
                    <div className="flex items-center gap-2 text-base-content/70">
                        <FontAwesomeIcon
                            icon={faPhone}
                            className="w-3 h-3 text-base-content/40 flex-shrink-0"
                        />
                        <div className="flex items-center gap-1">
                            <span>{task.patient_phone_number}</span>
                            <WhatsAppReminder link={task.whatsapp_link} />
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-2 text-base-content/70">
                    <FontAwesomeIcon
                        icon={faExclamation}
                        className="w-3 h-3 text-base-content/40 flex-shrink-0"
                    />
                    <div className="flex items-center gap-2">
                        <span>Prioridad:</span>
                        {getPriorityBadge(task.priority)}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-base-content/70">
                    <FontAwesomeIcon
                        icon={faCheck}
                        className="w-3 h-3 text-base-content/40 flex-shrink-0"
                    />
                    <span>{task.finished ? "Finalizada" : "Pendiente"}</span>
                </div>
            </div>
        );
    };

    const formConfig = {
        topic: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Tema",
            placeholder: "Tema",
            name: "topic",
        },
        order: {
            condition: true,
            disabled: false,
            type: "selector",
            label: "Orden",
            options: [
                { key: "date_asc", label: "Fecha - ascendente" },
                { key: "date_desc", label: "Fecha - descendente" },
                { key: "priority_asc", label: "Prioridad - ascendente" },
                { key: "priority_desc", label: "Prioridad - descendente" },
            ],
            defaultValue: "date_asc",
            name: "order",
        },
        temporality: {
            condition: true,
            disabled: false,
            type: "selector",
            name: "temporality",
            label: "Temporalidad",
            options: [
                { key: "all", label: "Todas" },
                { key: "current", label: "Actuales" },
                { key: "future", label: "Posteriores a la fecha actual" },
                { key: "past", label: "Anteriores a la fecha actual" },
                { key: "custom", label: "Filtro personalizado" },
            ],
            defaultValue: "all",
        },
        period: {
            condition: ["future", "past", "current"].includes(writtenFilter.temporality),
            disabled: false,
            type: "selector",
            name: "period",
            label: "Periodo temporal",
            options: [
                { key: "days", label: "Día" },
                { key: "weeks", label: "Semana" },
                { key: "months", label: "Mes" },
                { key: "years", label: "Año" },
            ],
            defaultValue: "days",
        },
        duration: {
            condition: ["future", "past"].includes(writtenFilter.temporality),
            disabled: false,
            type: "number",
            minValue: 1,
            label: "Duración",
            placeholder: "Ilimitado",
            name: "duration",
            info: "Número de días, semanas, meses o años que quieres que dure el filtro. Si no lo rellenas, se tomará como ilimitado y se ignorará el periodo temporal",
        },
        starting_date: {
            condition: writtenFilter.temporality === "custom",
            disabled: false,
            type: "date",
            label: "Fecha incial",
            name: "starting_date",
        },
        finishing_date: {
            condition: writtenFilter.temporality === "custom",
            disabled: false,
            type: "date",
            label: "Fecha final",
            name: "finishing_date",
        },
        dummy: {
            condition: writtenFilter.temporality !== "current",
            type: "dummy",
        },
        display_finished: {
            condition: true,
            disabled: false,
            label: "Finalizadas",
            info: "Si lo seleccionas se mostrarán las tareas aunque estén finalizadas",
            type: "checkbox",
            name: "display_finished",
        },
        exclude_autogenerated: {
            condition: true,
            disabled: false,
            label: "Excluir autogeneradas",
            info: "Si lo seleccionas, se ocultarán las tareas que han sido generadas automáticamente por el sistema",
            type: "checkbox",
            name: "exclude_autogenerated",
        },
    };

    const tableState = getTableState(tasks);

    const renderContent = () => {
        if (tableState === "loading") {
            return <LoadingTable message="Cargando tareas..." />;
        }

        if (tableState === "empty") {
            return (
                <EmptyTable
                    icon={faTasks}
                    title="No hay tareas disponibles"
                    description="Añade la primera tarea para comenzar a gestionar las tareas de tu clínica."
                    buttonText="Nueva tarea"
                    onButtonClick={handleSubmit(navigate, "/task/register")}
                />
            );
        }

        return (
            <>
                <OverviewCardList
                    data={tasks}
                    navigate={navigate}
                    getDetailPath={getDetailPath}
                    renderTitle={renderTitle}
                    renderSubtitle={renderSubtitle}
                    renderFooter={renderFooter}
                    renderActions={renderCardActions}
                />
                <OverviewTable
                    data={tasks}
                    navigate={navigate}
                    columns={columns}
                    getRowData={getRowData}
                    getDetailPath={getDetailPath}
                    renderActions={renderTableActions}
                    actionsWidth="w-[12%]"
                    noTruncateColumns={[0]}
                />
            </>
        );
    };

    return (
        <div className="sm:container p-3 mx-auto mt-5 mb-12">
            <h2 className="sr-only">
                Consulta las tareas de tu clínica de fisioterapia en MiFisio Agenda - Tareas
            </h2>

            <Collapse isMainTitle={true} title="Tareas" faModality="filter">
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="mt-6">
                <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* New task button */}
                    <button
                        className="btn btn-primary btn-sm w-full sm:w-auto"
                        onClick={handleSubmit(navigate, "/task/register")}
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Nueva tarea
                    </button>

                    {/* Pagination - only render wrapper if there's pagination or total items */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={tasks.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination for small screens - duplicated to improve UX */}
                <div className="w-full my-5 sm:w-auto lg:hidden">
                    <PaginatorComponent
                        pagination={tasks.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                        showTotalItems={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default OverviewTasks;
